const KEY = 'worker-verify';
const ENTRY = 'xaWorkerProofEntry';
const PATHS = ['/pro', '/installer'];
let pendingProof = null;
let watching = false;
const valid = token => /^[A-Za-z0-9_-]{64}$/.test(token);
const empty = () => ({ present: false, token: '', invalid: false });

function matchingPending() {
  if (typeof window === 'undefined') return null;
  if (pendingProof && (pendingProof.path !== window.location.pathname || pendingProof.entry !== window.history.state?.[ENTRY])) pendingProof = null;
  return pendingProof;
}
function watchNavigation() {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  // A different path or a bare/new history entry cannot recover a proof.
  window.addEventListener('popstate', matchingPending);
  window.addEventListener('hashchange', matchingPending);
}
function marker() {
  return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('');
}

// Only the noncredential entry marker goes into history. The proof itself stays
// in this module until explicit verify/cancel, surviving matching React remounts.
function scrubWorkerVerificationFragment() {
  if (typeof window === 'undefined') return empty();
  watchNavigation();
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.slice(1));
  const present = fragment.has(KEY) || url.searchParams.has(KEY);
  if (!present) return empty();
  pendingProof = null;
  // Even an invalid or query-string link must not revive the prior account.
  try { window.sessionStorage.removeItem('xa.installer.session'); } catch { /* No stored account is usable here. */ }
  const values = [...fragment.getAll(KEY), ...url.searchParams.getAll(KEY)];
  const candidate = fragment.get(KEY) || '';
  const accepted = PATHS.includes(url.pathname) && fragment.getAll(KEY).length === 1 && !url.searchParams.has(KEY) && valid(candidate);
  url.searchParams.delete(KEY);
  for (const [key, value] of [...url.searchParams]) {
    if (values.some(secret => secret && (key.includes(secret) || value.includes(secret)))) url.searchParams.delete(key);
  }
  url.hash = '';
  let entry = '';
  try {
    const state = window.history.state;
    // Authentication navigation keeps only Router bookkeeping, never arbitrary
    // prior user state that could itself contain a credential.
    const safeState = {};
    if (Number.isSafeInteger(state?.idx)) safeState.idx = state.idx;
    if (typeof state?.key === 'string' && /^[A-Za-z0-9_-]{1,16}$/.test(state.key) && !values.some(secret => secret && state.key.includes(secret))) safeState.key = state.key;
    delete safeState[ENTRY];
    if (accepted) { entry = marker(); safeState[ENTRY] = entry; }
    window.history.replaceState(safeState, '', url.pathname + url.search);
  } catch {
    return { present: true, token: '', invalid: true };
  }
  if (accepted) pendingProof = { path: url.pathname, entry, token: candidate };
  return { present: true, token: accepted ? candidate : '', invalid: !accepted };
}

// Bootstrap before React/theme; no proof persists through a page refresh.
export function stageInitialWorkerVerification() { scrubWorkerVerificationFragment(); }

export function hasWorkerVerificationLink() {
  if (typeof window === 'undefined') return false;
  watchNavigation();
  const url = new URL(window.location.href);
  return !!matchingPending() || new URLSearchParams(url.hash.slice(1)).has(KEY) || url.searchParams.has(KEY);
}

export function consumeWorkerVerificationFragment() {
  const incoming = scrubWorkerVerificationFragment();
  if (incoming.present) return incoming;
  const pending = matchingPending();
  return pending ? { present: true, token: pending.token, invalid: false } : incoming;
}

// Atomic one-use take across multiple component instances. The history marker
// alone never authorizes a request and cannot restore a consumed/canceled proof.
export function takeWorkerVerificationProof(token) {
  const pending = matchingPending();
  if (!pending || pending.token !== token) return false;
  pendingProof = null;
  try {
    const state = { ...window.history.state };
    delete state[ENTRY];
    window.history.replaceState(state, '', window.location.pathname + window.location.search + window.location.hash);
  } catch { /* In-memory proof is already gone; a leftover marker is inert. */ }
  return true;
}
